import { useTheme } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { bindToggle } from 'material-ui-popup-state/core';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import {
  Icons,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  withProps,
  tokens,
} from '@cast/design-system';

import { ProfileAvatar } from 'components/avatars/ProfileAvatar';
import { Link as RouterLink, LogoutWrapper } from 'components/router';

import { HeaderButtonWithTooltip, HeaderPopover } from './_components';

import { getDocsLink } from '@cast/utils';

type ProfileMenuProps = {
  user: string;
  email: string;
};

const ProfileListItemText = withProps(ListItemText, {
  primaryTypographyProps: { variant: 'P12M' },
  secondaryTypographyProps: { variant: 'P10R' },
});

export const ProfileMenu = ({ user, email }: ProfileMenuProps) => {
  const { palette } = useTheme();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'profile-menu',
  });

  return (
    <>
      <HeaderButtonWithTooltip
        {...bindToggle(popupState)}
        active={popupState.isOpen}
        startIcon={<Icons.UserCircle />}
        label={capitalize(user)}
        testId="profile-menu-btn"
      />
      <HeaderPopover {...bindPopover(popupState)} testId="profile-menu">
        <List size="medium" disablePadding sx={{ minWidth: '190px' }}>
          <ListItem
            startAdornment={
              <ListItemIcon>
                <ProfileAvatar
                  name={user}
                  email={email}
                  size="md"
                  sx={{ cursor: 'default' }}
                />
              </ListItemIcon>
            }
            sx={{
              '&&': {
                borderBottom: tokens.theme.border.structural.neutralSubtle,
                backgroundColor: `${tokens.theme.bg.structural.surface} !important`,
              },
              '&&:hover, &&.Mui-focusVisible': {
                borderBottom: tokens.theme.border.structural.neutralSubtle,
                backgroundColor: `${tokens.theme.bg.structural.surface} !important`,
              },
              cursor: 'default',
            }}
            multiline
            disableHover
          >
            <ProfileListItemText primary={capitalize(user)} secondary={email} />
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component={RouterLink}
            testId="api-keys-link"
            to="/user/api-access"
            startAdornment={
              <Icons.Key
                weight="regular"
                color={tokens.theme.fg.structural.secondary}
              />
            }
            onClick={() => popupState.close()}
          >
            <ListItemText>API keys</ListItemText>
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            testId="api-documentation-link"
            target="_blank"
            rel="noreferrer noopener"
            href={getDocsLink('castai_swagger')}
            startAdornment={
              <Icons.Book
                weight="regular"
                color={tokens.theme.fg.structural.secondary}
              />
            }
            onClick={() => popupState.close()}
          >
            <ListItemText>API documentation</ListItemText>
          </ListItem>

          <LogoutWrapper>
            <ListItem
              testId="log-out-btn"
              sx={{ color: palette.red[500] }}
              startAdornment={
                <Icons.SignOut weight="regular" color="var(--red-500)" />
              }
            >
              <ListItemText>Log out</ListItemText>
            </ListItem>
          </LogoutWrapper>
        </List>
      </HeaderPopover>
    </>
  );
};
